import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { COMPANY } from "../../site/data";
import { LANDING_SEGMENTS, PROMO_MEDIUMS, PROMO_SOURCES } from "../../site/landing/content";

/* =========================================================
   랜딩페이지 관리 — 분야별 랜딩(/for/*)
   탭: 페이지 목록 · 짧은 링크 · 유입 통계  (?tab=pages|links|stats)
   상담 문의 자체는 '랜딩 문의'(LeadsPage)에서 본다.
   ========================================================= */
const SEGMENT_LABEL = {
  hub: "허브 (/for)",
  contact: "문의 페이지 (/for/contact)",
  "": "분야 미선택",
  ...Object.fromEntries(LANDING_SEGMENTS.map((s) => [s.slug, `${s.label}${s.ready ? "" : " (준비 중)"}`])),
};
const MEDIUM_LABEL = Object.fromEntries(PROMO_MEDIUMS);
const PERIODS = [[7, "7일"], [30, "30일"], [90, "90일"], [0, "전체"]];
const TABS = [["pages", "페이지 목록"], ["links", "짧은 링크"], ["stats", "유입 통계"]];

function rate(leads, visits) {
  if (!visits) return "—";
  return `${((leads / visits) * 100).toFixed(1)}%`;
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(/[Zz+]|[+-]\d{2}:\d{2}$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("ko-KR", { timeZone: "Asia/Seoul" });
}

async function copy(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    window.prompt("복사해서 쓰세요", text);
  }
}

/* --------------------------------------------------------
   페이지 목록 — 15개 분야 + 허브 · 문의 페이지
   -------------------------------------------------------- */
function PagesTab({ stats, onMakeLink }) {
  const bySeg = Object.fromEntries(((stats && stats.segments) || []).map((r) => [r.segment, r]));
  const pages = [
    { key: "hub", path: "/for", label: "허브 — 어떤 분이신가요?", ready: true, linkable: true },
    ...LANDING_SEGMENTS.map((s) => ({
      key: s.slug,
      path: s.ready ? `/for/${s.slug}` : `/for/contact?from=${s.slug}`,
      label: `${String(s.no).padStart(2, "0")} · ${s.label}`,
      ready: !!s.ready,
      wave: s.wave,
      linkable: !!s.ready,
    })),
    { key: "contact", path: "/for/contact", label: "공통 문의 페이지", ready: true },
  ];

  return (
    <>
      <div className="sstoa-panel">
        <table className="sstoa-table">
          <thead>
            <tr><th>페이지</th><th>상태</th><th>주소</th><th>방문 (30일)</th><th>문의 (30일)</th><th></th></tr>
          </thead>
          <tbody>
            {pages.map((p) => {
              const r = bySeg[p.key] || { visits: 0, leads: 0 };
              const full = `${COMPANY.origin}${p.path}`;
              return (
                <tr key={p.key}>
                  <td style={{ fontWeight: 600 }}>{p.label}</td>
                  <td>
                    {p.ready
                      ? <span className="sstoa-tag">공개</span>
                      : <span className="sstoa-tag sstoa-tag--gray">{p.wave}차 · 준비 중</span>}
                  </td>
                  <td style={{ fontSize: 12.5, wordBreak: "break-all" }}>
                    <a className="sstoa-link" href={p.path} target="_blank" rel="noopener noreferrer">{p.path}</a>
                  </td>
                  <td>{r.visits}</td>
                  <td>{p.key === "hub" || p.key === "contact" ? "—" : r.leads}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button className="sstoa-btn sstoa-btn--sm" onClick={() => copy(full)}>주소 복사</button>{" "}
                    {p.linkable && (
                      <button className="sstoa-btn sstoa-btn--sm sstoa-btn--primary" onClick={() => onMakeLink(p.key)}>
                        짧은 링크 만들기
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="sstoa-hint">
        준비 중인 분야는 허브에서 문의 페이지로 바로 연결됩니다(문의에 분야가 기록됨). 문구는 frontend/src/site/landing/content.js 에서 고칩니다.
      </p>
    </>
  );
}

/* --------------------------------------------------------
   짧은 링크 — 만들기 + 배포 링크 목록
   -------------------------------------------------------- */
const EMPTY_LINK = { segment: "", source: "", medium: "", campaign: "wave1", code: "", note: "" };

function LinkForm({ initialSegment, onCreated }) {
  const [f, setF] = useState({ ...EMPTY_LINK, segment: initialSegment || "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [made, setMade] = useState(null);
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  useEffect(() => {
    if (initialSegment) setF((v) => ({ ...v, segment: initialSegment }));
  }, [initialSegment]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const link = await api.post("/landing/links", f);
      setMade(link);
      setF((v) => ({ ...EMPTY_LINK, segment: v.segment, campaign: v.campaign }));
      onCreated(link);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="sstoa-panel sstoa-panel--pad" onSubmit={submit}>
      <div className="sstoa-form-cols">
        <div className="sstoa-field">
          <label>도착 페이지 *</label>
          <select className="sstoa-input" required value={f.segment} onChange={set("segment")}>
            <option value="">선택</option>
            <option value="hub">허브 (/for)</option>
            {LANDING_SEGMENTS.filter((s) => s.ready).map((s) => (
              <option key={s.slug} value={s.slug}>{s.label} (/for/{s.slug})</option>
            ))}
          </select>
        </div>
        <div className="sstoa-field">
          <label>홍보물 형태 (utm_medium) *</label>
          <select className="sstoa-input" required value={f.medium} onChange={set("medium")}>
            <option value="">선택</option>
            {PROMO_MEDIUMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="sstoa-field">
          <label>채널 (utm_source) *</label>
          <input className="sstoa-input" required list="landing-sources" placeholder="naver-cafe" value={f.source} onChange={set("source")} />
          <datalist id="landing-sources">
            {PROMO_SOURCES.map((s) => <option key={s} value={s} />)}
          </datalist>
        </div>
        <div className="sstoa-field">
          <label>캠페인 (utm_campaign)</label>
          <input className="sstoa-input" placeholder="wave1" value={f.campaign} onChange={set("campaign")} />
        </div>
        <div className="sstoa-field">
          <label>코드 (비우면 자동)</label>
          <input className="sstoa-input" placeholder="예: art-insta1" value={f.code} onChange={set("code")} />
        </div>
        <div className="sstoa-field">
          <label>게시 위치 메모</label>
          <input className="sstoa-input" placeholder="예: ○○ 창업 카페 홍보게시판" value={f.note} onChange={set("note")} />
        </div>
      </div>
      {error && <div className="sstoa-error" style={{ margin: "10px 0" }}>{error}</div>}
      <div className="sstoa-actions" style={{ marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
        <button className="sstoa-btn sstoa-btn--primary" disabled={busy}>짧은 링크 만들기</button>
        {made && (
          <>
            <strong style={{ fontSize: 14 }}>{COMPANY.origin}/go/{made.code}</strong>
            <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={() => copy(`${COMPANY.origin}/go/${made.code}`)}>복사</button>
          </>
        )}
      </div>
      <p className="sstoa-hint">
        채널 이름은 같은 곳이면 늘 같은 값으로 적어야 통계가 한 줄로 모입니다 (예: 네이버 카페는 모두 naver-cafe, 카페 이름은 메모에).
      </p>
    </form>
  );
}

function LinksTab({ initialSegment, onError }) {
  const [links, setLinks] = useState(null);

  async function load() {
    try {
      setLinks(await api.get("/landing/links"));
    } catch (err) { onError(err.message); }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function removeLink(id) {
    if (!window.confirm("이 짧은 링크를 지울까요? 이미 배포한 링크는 허브(/for)로 연결됩니다.")) return;
    try {
      await api.del(`/landing/links/${id}`);
      await load();
    } catch (err) { onError(err.message); }
  }

  return (
    <>
      <LinkForm initialSegment={initialSegment} onCreated={() => load()} />

      <div className="sstoa-section">
        <div className="sstoa-section__head"><h2>배포 링크</h2></div>
        <div className="sstoa-panel">
          {links === null ? (
            <div className="sstoa-empty">불러오는 중…</div>
          ) : links.length === 0 ? (
            <div className="sstoa-empty">만든 링크가 없습니다.</div>
          ) : (
            <table className="sstoa-table">
              <thead><tr><th>짧은 링크</th><th>도착</th><th>채널 · 형태</th><th>메모</th><th>클릭</th><th>만든 날</th><th></th></tr></thead>
              <tbody>
                {links.map((l) => {
                  const short = `${COMPANY.origin}/go/${l.code}`;
                  return (
                    <tr key={l.id}>
                      <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>/go/{l.code}</td>
                      <td>{SEGMENT_LABEL[l.segment] || l.segment}</td>
                      <td>{l.source} · {MEDIUM_LABEL[l.medium] || l.medium}{l.campaign ? ` · ${l.campaign}` : ""}</td>
                      <td>{l.note || "—"}</td>
                      <td>{l.clicks}</td>
                      <td>{formatDate(l.created_at)}</td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <button className="sstoa-btn sstoa-btn--sm" onClick={() => copy(short)}>짧은 링크 복사</button>{" "}
                        <button className="sstoa-btn sstoa-btn--sm" onClick={() => copy(`${COMPANY.origin}${l.target}`)}>전체 주소 복사</button>{" "}
                        <button className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => removeLink(l.id)}>삭제</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

/* --------------------------------------------------------
   유입 통계 — 분야별 · 채널별 방문 · 문의 · 전환율
   -------------------------------------------------------- */
function StatsTab({ days, stats, onPeriod }) {
  return (
    <>
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {PERIODS.map(([d, l]) => (
          <button key={d} className={`sstoa-btn sstoa-btn--sm${days === d ? " sstoa-btn--primary" : ""}`} onClick={() => onPeriod(d)}>
            {l}
          </button>
        ))}
      </div>

      {!stats ? (
        <div className="sstoa-panel"><div className="sstoa-empty">불러오는 중…</div></div>
      ) : (
        <>
          <div className="sstoa-stats">
            <div className="sstoa-stat"><div className="sstoa-stat__num">{stats.visits}</div><div className="sstoa-stat__label">방문</div></div>
            <div className="sstoa-stat"><div className="sstoa-stat__num">{stats.leads}</div><div className="sstoa-stat__label">문의</div></div>
            <div className="sstoa-stat"><div className="sstoa-stat__num">{rate(stats.leads, stats.visits)}</div><div className="sstoa-stat__label">전환율</div></div>
          </div>

          <div className="sstoa-section">
            <div className="sstoa-section__head"><h2>분야별</h2></div>
            <div className="sstoa-panel">
              {stats.segments.length === 0 ? (
                <div className="sstoa-empty">아직 기록이 없습니다.</div>
              ) : (
                <table className="sstoa-table">
                  <thead><tr><th>페이지</th><th>방문</th><th>문의</th><th>전환율</th></tr></thead>
                  <tbody>
                    {stats.segments.map((r) => (
                      <tr key={r.segment}>
                        <td>{SEGMENT_LABEL[r.segment] || r.segment}</td>
                        <td>{r.visits}</td>
                        <td>{r.leads}</td>
                        <td>{r.segment === "hub" || r.segment === "contact" ? "—" : rate(r.leads, r.visits)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <p className="sstoa-hint">
              방문은 브라우저 세션마다 페이지당 한 번 셉니다. 문의는 문의자가 고른 분야로 셉니다.
              허브 · 문의 페이지 방문은 흐름 확인용이라 전환율을 계산하지 않습니다.
            </p>
          </div>

          <div className="sstoa-section">
            <div className="sstoa-section__head"><h2>채널별</h2></div>
            <div className="sstoa-panel">
              {stats.sources.length === 0 ? (
                <div className="sstoa-empty">아직 기록이 없습니다.</div>
              ) : (
                <table className="sstoa-table">
                  <thead><tr><th>채널</th><th>홍보물 형태</th><th>방문</th><th>문의</th><th>전환율</th></tr></thead>
                  <tbody>
                    {stats.sources.map((r) => (
                      <tr key={`${r.source}|${r.medium}`}>
                        <td>{r.source}</td>
                        <td>{MEDIUM_LABEL[r.medium] || r.medium || "—"}</td>
                        <td>{r.visits}</td>
                        <td>{r.leads}</td>
                        <td>{rate(r.leads, r.visits)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default function LandingAdmin() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "pages";
  const [linkSegment, setLinkSegment] = useState("");
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [pageStats, setPageStats] = useState(null);
  const [error, setError] = useState("");

  async function loadStats(d) {
    try {
      const data = await api.get(`/landing/stats?days=${d}`);
      setStats(data);
      if (d === 30) setPageStats(data);
    } catch (err) { setError(err.message); }
  }

  useEffect(() => {
    loadStats(30);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function go(next) {
    setParams(next === "pages" ? {} : { tab: next }, { replace: true });
  }

  function makeLink(segment) {
    setLinkSegment(segment);
    go("links");
  }

  function onPeriod(d) {
    setDays(d);
    setStats(null);
    loadStats(d);
  }

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Link to="/sstoa" className="sstoa-back">← 홈</Link>
      </div>

      <div className="sstoa-topbar">
        <div>
          <h1 className="sstoa-title">랜딩페이지 관리</h1>
          <p className="sstoa-sub">
            분야별 홍보 랜딩(/for/*)의 주소 · 배포용 짧은 링크 · 유입 통계입니다.{" "}
            <Link to="/sstoa/leads">랜딩 문의 보기 →</Link>
          </p>
        </div>
      </div>

      <div className="sstoa-tabs" style={{ marginTop: 0, marginBottom: 16 }}>
        {TABS.map(([k, l]) => (
          <button key={k} type="button" className={`sstoa-tab${tab === k ? " is-active" : ""}`} onClick={() => go(k)}>
            {l}
          </button>
        ))}
      </div>

      {error && <div className="sstoa-error" style={{ marginBottom: 12 }}>{error}</div>}

      {tab === "pages" && <PagesTab stats={pageStats} onMakeLink={makeLink} />}
      {tab === "links" && <LinksTab initialSegment={linkSegment} onError={setError} />}
      {tab === "stats" && <StatsTab days={days} stats={stats} onPeriod={onPeriod} />}
    </div>
  );
}
